import React, { useState, useRef, useEffect } from 'react';
import config from '../config';
import '../styles/VoiceMessage.css';

// Simple global event target for coordinating playback
const voiceBus = typeof window !== 'undefined' ? (window.__VOICE_BUS__ || (window.__VOICE_BUS__ = new EventTarget())) : null;

const VoiceMessage = ({ message, isMe }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const idRef = useRef(message._id || Math.random().toString(36).slice(2));

  useEffect(() => {
    const audio = audioRef.current; if (!audio) return;
    const applyDuration = () => { if (audio && isFinite(audio.duration) && audio.duration > 0) { setDuration(audio.duration); return true; } return false; };
    const updateTime = () => setCurrentTime(audio.currentTime || 0);
    const tryUpdateDuration = () => { applyDuration(); };
    const handleEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    ['loadedmetadata','durationchange','canplay','canplaythrough','loadeddata'].forEach(ev => audio.addEventListener(ev, tryUpdateDuration));
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('ended', handleEnded);
    let attempts = 0; const poll = setInterval(() => { if (applyDuration() || attempts++ > 10) clearInterval(poll); }, 400);

    // Listen for global pause events
    const handleGlobalPlay = (e) => {
      if (e.detail && e.detail.id !== idRef.current && audio && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    };
    voiceBus && voiceBus.addEventListener('voice:play', handleGlobalPlay);

    return () => {
      clearInterval(poll);
      ['loadedmetadata','durationchange','canplay','canplaythrough','loadeddata'].forEach(ev => audio.removeEventListener(ev, tryUpdateDuration));
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('ended', handleEnded);
      voiceBus && voiceBus.removeEventListener('voice:play', handleGlobalPlay);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current; if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // announce intent to play (others will pause)
      voiceBus && voiceBus.dispatchEvent(new CustomEvent('voice:play', { detail: { id: idRef.current } }));
      audio.play();
      setIsPlaying(true);
    }
  };

  const formatSecs = (t) => {
    if (!t || !isFinite(t) || t < 0) return '0.00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2,'0');
    return `${m}.${s}`; // minutes.seconds
  };
  const effectiveDuration = duration || (isFinite(message.voiceDuration) ? Number(message.voiceDuration) : 0);
  const getProgress = () => { if (!effectiveDuration || !isFinite(effectiveDuration)) return 0; return (currentTime / effectiveDuration) * 100; };

  return (
    <div className="voice-message">
      <audio ref={audioRef} src={`${config.apiUrl}${message.voiceUrl}`} preload="metadata" />
      <button className={`voice-play-btn ${isPlaying ? 'playing' : ''}`} onClick={togglePlay} aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}>
        {isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5,3 19,12 5,21"/></svg>
        )}
      </button>
      <div className="voice-content">
        <div className="voice-waveform">
          <div className="voice-bars base" />
            <div className="voice-bars fill" style={{ width: `${getProgress()}%` }} />
        </div>
        <div className="voice-duration">{formatSecs(currentTime)}/{formatSecs(effectiveDuration)}</div>
      </div>
    </div>
  );
};

export default VoiceMessage;